<template>
  <div v-if="isVisible" class="fixed inset-0 bg-gray-600 bg-opacity-70 z-50 flex justify-center items-center p-4">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-semibold dark:text-gray-100 flex items-center gap-2">
          <svg class="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
            <path d="M18 3a1 1 0 00-1.45-.9L7 6.5H4a2 2 0 00-2 2v3a2 2 0 002 2h1l1.2 3.6a1 1 0 001.9-.3V13l7.45 3.9A1 1 0 0018 16V3z" />
          </svg>
          {{ isEditing ? 'Edit broadcast' : 'New broadcast' }}
        </h2>
        <button class="text-gray-400 hover:text-red-500 text-xl leading-none disabled:opacity-50" :disabled="saving" @click="close">&times;</button>
      </div>

      <!-- Every input is locked while publishing/saving -->
      <fieldset :disabled="saving" class="disabled:opacity-60">

      <!-- Clients -->
      <div class="mb-4" v-click-outside="closeClientDropdown">
        <label class="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-100 mb-2">
          <input type="checkbox" v-model="form.all_companies" class="form-checkbox h-4 w-4 text-blue-600 rounded" />
          Send to all clients
        </label>
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">
          Client(s) <span class="text-gray-400">— type to search; add more than one for a shared incident</span>
        </label>
        <div class="relative">
          <input type="text" ref="clientInput" v-model="clientQuery" :disabled="form.all_companies"
            @focus="!form.all_companies && (dropdownState.client.visible = true)"
            @keydown.down.prevent="onKeyDown('client', 'down')" @keydown.up.prevent="onKeyDown('client', 'up')"
            @keydown.enter.prevent="onEnter('client', addClient)"
            :placeholder="form.all_companies ? 'All clients selected' : 'Start typing a client name…'"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed dark:disabled:bg-gray-600" />
          <ul v-if="!form.all_companies && dropdownState.client.visible && filteredClients.length"
            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-full max-h-48 overflow-y-auto dark:bg-gray-400 dark:text-gray-100 scrollable-requester-list">
            <li v-for="(c, index) in filteredClients" :key="c.id" @click="addClient(c)"
              :ref="el => setDropdownItemRef(el, index)"
              :class="['cursor-pointer p-3', dropdownState.client.index === index ? 'bg-blue-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800']">
              {{ c.companyname }}
            </li>
          </ul>
        </div>
        <div v-if="!form.all_companies" class="flex flex-wrap gap-1 mt-2">
          <span v-for="c in selectedClients" :key="c.id"
            class="inline-flex items-center gap-1 bg-blue-100 text-gray-700 rounded-full px-2 py-0.5 text-xs font-semibold dark:bg-blue-900 dark:text-gray-100">
            {{ c.companyname }}
            <button @click="removeClient(c)" class="text-sm leading-none hover:text-red-500">&times;</button>
          </span>
        </div>
        <p v-else class="text-xs text-gray-500 dark:text-gray-300 mt-2">
          This broadcast will show for <strong>every client</strong>.
        </p>
      </div>

      <!-- Type -->
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-100 mb-1">Type</label>
        <div class="flex gap-2">
          <button type="button" @click="form.type = 'info'"
            :class="typeBtnClass('info', 'blue')">Informational</button>
          <button type="button" @click="form.type = 'outage'"
            :class="typeBtnClass('outage', 'red')">Outage</button>
        </div>
      </div>

      <!-- Subject -->
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Subject</label>
        <input type="text" v-model="form.subject" maxlength="255"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100" />
      </div>

      <!-- Message -->
      <div class="mb-4">
        <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Message to customers</label>
        <textarea v-model="form.message" rows="3" :maxlength="MESSAGE_MAX"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"></textarea>
        <p class="text-xs mt-1 text-right" :class="form.message.length >= MESSAGE_MAX ? 'text-red-600' : 'text-gray-400'">
          {{ form.message.length }} / {{ MESSAGE_MAX }} characters
        </p>
      </div>

      <!-- Auto-hide + ticket (both optional, for either type) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Auto-hide</label>
          <label class="mt-1 flex items-center gap-2 text-xs text-gray-700 dark:text-gray-100">
            <input type="checkbox" v-model="form.auto_hide_24h" class="form-checkbox h-4 w-4 text-blue-600 rounded" />
            Auto-hide after 24 hours
          </label>
          <p class="text-xs text-gray-400 mt-1">
            Otherwise it stays until you end it — or, if you link a ticket, when that ticket is closed.
          </p>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">
            Linked ticket <span class="text-gray-400">(optional)</span>
          </label>
          <input type="number" v-model="form.ticket_id" placeholder="Ticket # (optional)" @input="ticketError = ''"
            class="mt-1 block w-full rounded-md shadow-sm text-xs dark:bg-gray-400 dark:text-gray-100 dark:placeholder-gray-100"
            :class="ticketError ? 'border-red-500 ring-1 ring-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-900'" />
          <p v-if="ticketError" class="text-xs text-red-600 dark:text-red-400 mt-1">{{ ticketError }}</p>
          <p v-else class="text-xs text-gray-400 mt-1">
            Optional — if set, closing this ticket ends the broadcast. Leave blank for a multi-company broadcast and end it manually.
          </p>
        </div>
      </div>

      <!-- Options -->
      <label v-if="!isEditing" class="flex items-start gap-2 p-2 border border-gray-200 dark:border-gray-700 rounded-md mb-2 bg-gray-50 dark:bg-gray-700">
        <input type="checkbox" v-model="form.email_on_post" class="mt-0.5 form-checkbox h-4 w-4 text-blue-600 rounded" />
        <span>
          <span class="block text-xs font-semibold text-gray-700 dark:text-gray-100">Email everyone at this client now</span>
          <span class="block text-xs text-gray-500 dark:text-gray-300">Sends the broadcast to all users, admins &amp; contacts of the selected companies.</span>
        </span>
      </label>
      <label class="flex items-start gap-2 p-2 border border-gray-200 dark:border-gray-700 rounded-md mb-4 bg-gray-50 dark:bg-gray-700">
        <input type="checkbox" v-model="form.notify_company" class="mt-0.5 form-checkbox h-4 w-4 text-blue-600 rounded" />
        <span>
          <span class="block text-xs font-semibold text-gray-700 dark:text-gray-100">Notify the whole company when this ends</span>
          <span class="block text-xs text-gray-500 dark:text-gray-300">Sends an "all clear" to everyone at the client (except the ticket's requester &amp; watchers, who already get the close email).</span>
        </span>
      </label>

      </fieldset>

      <p v-if="error" class="text-xs text-red-600 mb-2">{{ error }}</p>

      <div class="flex justify-end gap-2">
        <button @click="close" :disabled="saving"
          class="text-xs font-medium py-2 px-4 rounded border border-gray-300 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
          Cancel
        </button>
        <button @click="submit" :disabled="saving"
          class="inline-flex items-center px-4 py-2 text-xs font-medium rounded-md shadow-sm text-white bg-red-500 hover:bg-red-700 disabled:opacity-50">
          {{ saving ? (isEditing ? 'Saving…' : 'Publishing…') : (isEditing ? 'Save changes' : '📢 Publish broadcast') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick, getCurrentInstance } from 'vue';
import axios from 'axios';
import BroadcastService from '@/services/BroadcastService';

const props = defineProps({
  isVisible: { type: Boolean, default: false },
  // Optionally pre-fill from the ticket the manager is looking at.
  presetClient: { type: Object, default: null },
  presetTicketId: { type: [Number, String], default: null },
  // When set, the modal edits this existing broadcast instead of creating one.
  editBroadcast: { type: Object, default: null },
});
const emit = defineEmits(['close', 'created']);

const isEditing = computed(() => !!(props.editBroadcast && props.editBroadcast.id));

const clients = ref([]);
const clientQuery = ref('');
const clientInput = ref(null);
const selectedClients = ref([]);

// Same type-to-search mechanism the ticket screens use: a per-field
// { visible, index } state, arrow-key cycling, Enter-to-select, and
// scroll-the-highlighted-item-into-view.
const dropdownState = reactive({ client: { visible: false, index: -1 } });
const clientItemRefs = ref([]);
const setDropdownItemRef = (el, index) => { if (el) clientItemRefs.value[index] = el; };
const saving = ref(false);
// Linked-ticket problem from the server (missing / closed), shown under that field.
const ticketError = ref('');
// Must match the backend's `message` max rule.
const MESSAGE_MAX = 5000;
const { proxy } = getCurrentInstance();
const updateLoading = (n) => proxy?.$updateLoading?.(n);
const error = ref('');

const form = reactive({
  type: 'outage',
  all_companies: false,
  subject: '',
  message: '',
  ticket_id: '',
  auto_hide_24h: false,
  email_on_post: true,
  notify_company: false,
});

const filteredClients = computed(() => {
  const q = clientQuery.value.trim().toLowerCase();
  const chosen = new Set(selectedClients.value.map(c => c.id));
  return clients.value.filter(c =>
    !chosen.has(c.id) && (q === '' || (c.companyname || '').toLowerCase().includes(q))
  );
});

const typeBtnClass = (val, color) => {
  const active = form.type === val;
  const base = 'flex-1 border rounded-md py-2 text-xs font-bold';
  if (!active) return `${base} border-gray-300 text-gray-700 bg-white dark:bg-gray-700 dark:text-gray-100`;
  return color === 'red'
    ? `${base} border-transparent text-white bg-red-500`
    : `${base} border-transparent text-white bg-blue-600`;
};

const onKeyDown = (field, direction) => {
  const state = dropdownState[field];
  const list = filteredClients.value;
  if (!state.visible || !list.length) return;
  if (direction === 'down') state.index = (state.index + 1) % list.length;
  else if (direction === 'up') state.index = (state.index - 1 + list.length) % list.length;
};

const onEnter = (field, selectFn) => {
  const state = dropdownState[field];
  const list = filteredClients.value;
  if (state.index >= 0 && state.index < list.length) {
    selectFn(list[state.index]);
    state.index = -1;
  }
};

const closeClientDropdown = () => {
  dropdownState.client.visible = false;
  dropdownState.client.index = -1;
};

const addClient = (c) => {
  selectedClients.value.push(c);
  clientQuery.value = '';
  dropdownState.client.index = -1;
  // Keep the field focused and the list open so another client can be added
  // straight away without having to click back into the input.
  dropdownState.client.visible = true;
  nextTick(() => clientInput.value?.focus());
};

const removeClient = (c) => {
  selectedClients.value = selectedClients.value.filter(x => x.id !== c.id);
};

// Scroll the highlighted item into view as the index changes (same as the
// ticket screens).
watch(() => dropdownState.client.index, async (newIndex) => {
  await nextTick();
  const el = clientItemRefs.value[newIndex];
  if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
});

const fetchClients = async () => {
  if (clients.value.length) return;
  try {
    const { data } = await axios.get('clients');
    clients.value = Array.isArray(data) ? data : (data.data || []);
  } catch (e) {
    // Non-fatal: the manager can still type, just without suggestions.
  }
};

const resetForm = () => {
  clientQuery.value = '';
  error.value = '';
  ticketError.value = '';

  if (isEditing.value) {
    const b = props.editBroadcast;
    form.type = b.type || 'outage';
    form.all_companies = !!b.all_companies;
    form.subject = b.subject || '';
    form.message = b.message || '';
    form.ticket_id = b.ticket_id || '';
    form.auto_hide_24h = !!b.auto_hide_at;
    form.email_on_post = false;          // editing never re-blasts
    form.notify_company = !!b.notify_company;
    selectedClients.value = (b.companies || []).map(c => ({ id: c.id, companyname: c.companyname }));
    return;
  }

  form.type = 'outage';
  form.all_companies = false;
  form.subject = '';
  form.message = '';
  form.ticket_id = props.presetTicketId || '';
  form.auto_hide_24h = false;
  form.email_on_post = true;
  form.notify_company = false;
  selectedClients.value = props.presetClient && props.presetClient.id
    ? [{ id: props.presetClient.id, companyname: props.presetClient.companyname }]
    : [];
};

// Reset the highlight (and stale item refs) as the filtered list changes while typing.
watch(clientQuery, () => { dropdownState.client.index = -1; clientItemRefs.value = []; });

watch(() => props.isVisible, (open) => {
  if (open) {
    fetchClients();
    resetForm();
  }
});

const close = () => emit('close');

const submit = async () => {
  if (saving.value) return;
  error.value = '';
  ticketError.value = '';
  if (!form.all_companies && !selectedClients.value.length) { error.value = 'Pick at least one client, or choose all clients.'; return; }
  if (!form.subject.trim()) { error.value = 'Enter a subject.'; return; }
  if (!form.message.trim()) { error.value = 'Enter a message.'; return; }

  saving.value = true;
  updateLoading(1);
  try {
    const payload = {
      type: form.type,
      all_companies: form.all_companies,
      subject: form.subject.trim(),
      message: form.message.trim(),
      company_ids: form.all_companies ? [] : selectedClients.value.map(c => c.id),
      ticket_id: form.ticket_id ? Number(form.ticket_id) : null,
      email_on_post: form.email_on_post,
      notify_company: form.notify_company,
      auto_hide_24h: form.auto_hide_24h,
    };
    const { data } = isEditing.value
      ? await BroadcastService.update(props.editBroadcast.id, payload)
      : await BroadcastService.create(payload);
    emit('created', data);
    emit('close');
  } catch (e) {
    // A bad linked ticket is shown under the ticket field; anything else at the bottom.
    const ticketMsg = e?.response?.data?.errors?.ticket_id?.[0];
    if (ticketMsg) ticketError.value = ticketMsg;
    else error.value = e?.response?.data?.message || 'Could not save the broadcast.';
  } finally {
    saving.value = false;
    updateLoading(-1);
  }
};
</script>
